import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Check, KeyRound, Play, Plus, RefreshCw, Search, Trash2, Workflow } from "lucide-react"
import * as React from "react"
import { type Resolver, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { isApiError } from "@/api/client"
import type { Automation } from "@/api/generated/Automation"
import type { InvokeResult } from "@/api/generated/InvokeResult"
import type { Param } from "@/api/generated/Param"
import { describeError } from "@/api/mutation"
import { useAgents } from "@/api/queries/agents"
import {
  automationKeys,
  useAutomations,
  useCreateAutomation,
  useDeleteAutomation,
  useInvokeAutomation,
  useRotateAutomationSecret,
  useUpdateAutomation,
} from "@/api/queries/automations"
import { CodeBlock } from "@/components/app/code-block"
import { useConfirm } from "@/components/app/confirm-dialog"
import { CopyButton } from "@/components/app/copy-button"
import { FieldShell, SelectField, SwitchField, TextareaField, TextField } from "@/components/app/fields"
import { FormRoot, FormRootError, SubmitButton, UnsavedChangesBar } from "@/components/app/form"
import { Markdown } from "@/components/app/markdown"
import { Panel } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { SecretReveal } from "@/components/app/secret-reveal"
import { EmptyState, ErrorState, FormSkeleton } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { isParamType, ParamEditor, paramSchema } from "@/features/automations/param-editor"
import { TemplateEditor } from "@/features/chat/template-editor"
import { automationCurl, schemaIssues, templateIssues } from "@/features/chat/templates"
import { useDocumentTitle } from "@/hooks/use-document-title"
import { useIsMobile } from "@/hooks/use-mobile"
import i18n from "@/i18n"
import { cn } from "@/lib/utils"
import { z } from "@/lib/zod"

interface AutomationsSearch {
  id?: number | "new"
}

export const Route = createFileRoute("/_app/studio/automations")({
  staticData: { titleKey: "nav.items.automations" },
  validateSearch: (s: Record<string, unknown>): AutomationsSearch => {
    if (s.id === "new") return { id: "new" }
    const id = Number(s.id)
    return Number.isInteger(id) && id > 0 ? { id } : {}
  },
  component: AutomationsPage,
})

function hookUrl(publicId: string) {
  return `${window.location.origin}/api/hooks/automations/${publicId}`
}

function AutomationsPage() {
  const { t } = useTranslation()
  useDocumentTitle(t("chat.automations.title"))
  const { id } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const list = useAutomations()
  const isMobile = useIsMobile()
  const [q, setQ] = React.useState("")
  // lives here: creating an automation remounts the editor (new search param)
  const [secret, setSecret] = React.useState<string | null>(null)
  const select = (next: number | "new" | undefined) => void navigate({ search: next ? { id: next } : {} })

  const filtered = (list.data ?? []).filter((a) =>
    `${a.name} ${a.agent_name} ${a.description}`.toLowerCase().includes(q.trim().toLowerCase()),
  )
  const showList = !isMobile || id == null
  const showDetail = !isMobile || id != null

  return (
    <div className="flex h-full min-h-0">
      {showList && (
        <aside className="flex w-full shrink-0 flex-col border-r md:w-80 lg:w-88">
          <div className="space-y-3 border-b p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Workflow className="text-muted-foreground size-4" />
                <h1 className="truncate text-base font-semibold tracking-tight">
                  {t("chat.automations.title")}
                </h1>
              </div>
              <Button size="sm" onClick={() => select("new")} data-testid="automation-new">
                <Plus />
                {t("chat.automations.new")}
              </Button>
            </div>
            <div className="relative">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("chat.automations.search")}
                aria-label={t("chat.automations.search")}
                className="h-8 pl-8"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {list.isPending ? (
              <div className="space-y-2 p-2">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-14 rounded-lg" />
                ))}
              </div>
            ) : list.isError ? (
              <ErrorState error={list.error} onRetry={() => void list.refetch()} variant="plain" />
            ) : filtered.length === 0 ? (
              <p className="text-muted-foreground px-3 py-8 text-center text-sm">
                {q ? t("chat.automations.noMatches") : t("chat.automations.emptyList")}
              </p>
            ) : (
              <ul className="space-y-0.5">
                {filtered.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => select(a.id)}
                      aria-current={id === a.id ? "true" : undefined}
                      data-testid="automation-row"
                      className={cn(
                        "hover:bg-muted/60 flex w-full flex-col gap-1 rounded-lg px-3 py-2.5 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                        id === a.id && "bg-muted",
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium">{a.name}</span>
                        {a.api_invocation_enabled && (
                          <StatusBadge tone="info" className="ml-auto">
                            {t("chat.automations.api")}
                          </StatusBadge>
                        )}
                      </span>
                      <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
                        <span className="truncate">{a.agent_name}</span>
                        <span aria-hidden>·</span>
                        <span className="shrink-0">
                          {t("chat.automations.paramCount", { count: a.parameter_schema.length })}
                        </span>
                        <span aria-hidden>·</span>
                        <span className="shrink-0 tabular-nums">
                          {t("chat.automations.runCount", { count: a.invocation_count })}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      )}
      {showDetail && (
        <section className="min-w-0 flex-1 overflow-y-auto">
          {id == null ? (
            <div className="flex h-full items-center justify-center p-6">
              <EmptyState
                icon={Workflow}
                title={
                  list.data?.length ? t("chat.automations.selectTitle") : t("chat.automations.emptyTitle")
                }
                description={t("chat.automations.emptyDescription")}
                action={
                  <Button size="sm" onClick={() => select("new")}>
                    <Plus />
                    {t("chat.automations.new")}
                  </Button>
                }
              />
            </div>
          ) : (
            <AutomationDetail
              key={String(id)}
              automation={id === "new" ? null : (list.data?.find((a) => a.id === id) ?? undefined)}
              loading={list.isPending}
              onBack={() => select(undefined)}
              onCreated={(a) => select(a.id)}
              onDeleted={() => select(undefined)}
              onSecret={setSecret}
            />
          )}
        </section>
      )}
      <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
        <DialogContent className="sm:max-w-xl" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="bg-success/15 text-success flex size-6 items-center justify-center rounded-full">
                <Check className="size-3.5" />
              </span>
              {t("chat.automations.secretTitle")}
            </DialogTitle>
            <DialogDescription>{t("chat.automations.secretDescription")}</DialogDescription>
          </DialogHeader>
          {secret && <SecretReveal secret={secret} />}
          <DialogFooter>
            <Button onClick={() => setSecret(null)} data-testid="secret-done">
              {t("chat.automations.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------- editor

const schema = z
  .object({
    name: z.string().trim().min(1).max(255),
    description: z.string().max(2000),
    agent_id: z
      .number({ error: () => i18n.t("validation.required") })
      .int()
      .positive(),
    parameter_schema: z.array(paramSchema),
    additional_instructions_template: z.string(),
    initial_message_template: z.string(),
    api_invocation_enabled: z.boolean(),
  })
  .superRefine((v, ctx) => {
    for (const [i, kind] of schemaIssues(v.parameter_schema as Param[])) {
      ctx.addIssue({
        code: "custom",
        path: ["parameter_schema", i, "name"],
        message: i18n.t(
          kind === "name" ? "chat.automations.params.invalidName" : "chat.automations.params.duplicate",
        ),
      })
    }
    for (const f of ["additional_instructions_template", "initial_message_template"] as const) {
      if (templateIssues(v[f], v.parameter_schema as Param[]).length)
        ctx.addIssue({ code: "custom", path: [f], message: i18n.t("chat.automations.issues.fix") })
    }
  })
type Values = z.infer<typeof schema>

function toValues(a: Automation | null): Values {
  return {
    name: a?.name ?? "",
    description: a?.description ?? "",
    agent_id: a?.agent_id ?? (undefined as unknown as number),
    parameter_schema: (a?.parameter_schema ?? []).map((p) => ({
      name: p.name,
      label: p.label,
      type: isParamType(p.type) ? p.type : "string",
      required: p.required,
      default: p.default,
    })),
    additional_instructions_template: a?.additional_instructions_template ?? "",
    initial_message_template: a?.initial_message_template ?? "",
    api_invocation_enabled: a?.api_invocation_enabled ?? false,
  }
}

function AutomationDetail({
  automation,
  loading,
  onBack,
  onCreated,
  onDeleted,
  onSecret,
}: {
  /** `null` = new, `undefined` = not found (yet). */
  automation: Automation | null | undefined
  loading: boolean
  onBack: () => void
  onCreated: (a: Automation) => void
  onDeleted: () => void
  /** One-time webhook secret returned by create / enable / rotate. */
  onSecret: (secret: string) => void
}) {
  const { t } = useTranslation()
  const isNew = automation === null
  const agents = useAgents()
  const create = useCreateAutomation()
  const update = useUpdateAutomation()
  const remove = useDeleteAutomation()
  const rotate = useRotateAutomationSecret()
  const confirm = useConfirm()
  const qc = useQueryClient()
  const [runOpen, setRunOpen] = React.useState(false)
  const form = useForm<Values>({
    resolver: zodResolver(schema) as Resolver<Values>,
    defaultValues: toValues(automation ?? null),
  })
  const watchedSchema = useWatch({ control: form.control, name: "parameter_schema" }) as Param[]
  const apiEnabled = useWatch({ control: form.control, name: "api_invocation_enabled" })
  const messageTpl = useWatch({ control: form.control, name: "initial_message_template" })
  const instructionsTpl = useWatch({ control: form.control, name: "additional_instructions_template" })

  // follow server updates (e.g. after save) while the form is pristine
  React.useEffect(() => {
    if (automation && !form.formState.isDirty) form.reset(toValues(automation))
  }, [automation, form])

  if (automation === undefined)
    return loading ? (
      <div className="p-6">
        <FormSkeleton fields={6} />
      </div>
    ) : (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          icon={Workflow}
          title={t("chat.automations.notFound")}
          action={
            <Button size="sm" variant="outline" onClick={onBack}>
              <ArrowLeft />
              {t("chat.automations.back")}
            </Button>
          }
        />
      </div>
    )

  const onSubmit = async (v: Values) => {
    const body = { ...v, parameter_schema: v.parameter_schema.map((p) => ({ ...p, label: p.label.trim() })) }
    if (isNew) {
      const a = await create.mutateAsync(body)
      if (a.invocation_secret) onSecret(a.invocation_secret)
      form.reset(toValues(a))
      onCreated(a)
    } else {
      const a = await update.mutateAsync({ id: automation.id, ...body })
      if (a.invocation_secret) onSecret(a.invocation_secret)
      form.reset(toValues(a))
    }
  }

  const onDelete = () =>
    automation &&
    confirm({
      title: t("chat.automations.deleteTitle", { name: automation.name }),
      description: t("chat.automations.deleteDescription"),
      confirmLabel: t("chat.automations.delete"),
      destructive: true,
      confirmText: automation.name,
      onConfirm: async () => {
        await remove.mutateAsync(automation)
        qc.removeQueries({ queryKey: automationKeys.detail(automation.id) })
        onDeleted()
      },
    })

  const onRotate = () =>
    automation &&
    confirm({
      title: t("chat.automations.rotateTitle"),
      description: t("chat.automations.rotateDescription"),
      confirmLabel: t("chat.automations.rotate"),
      destructive: true,
      onConfirm: async () => {
        const a = await rotate.mutateAsync(automation.id)
        if (a.invocation_secret) onSecret(a.invocation_secret)
      },
    })

  const templates = [
    {
      name: "initial_message_template" as const,
      value: messageTpl,
      label: t("chat.automations.templates.message"),
      hint: t("chat.automations.templates.messageHint"),
      placeholder: t("chat.automations.templates.messagePlaceholder", {
        topic: "{{ topic }}",
        audience: "{{ audience }}",
      }),
      rows: 5,
    },
    {
      name: "additional_instructions_template" as const,
      value: instructionsTpl,
      label: t("chat.automations.templates.instructions"),
      hint: t("chat.automations.templates.instructionsHint"),
      placeholder: t("chat.automations.templates.instructionsPlaceholder"),
      rows: 4,
    },
  ]

  return (
    <FormRoot
      form={form}
      onSubmit={onSubmit}
      className="mx-auto w-full max-w-4xl px-4 pt-5 pb-10 sm:px-6 lg:px-8"
    >
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="-ml-1 md:hidden"
            onClick={onBack}
            aria-label={t("chat.automations.back")}
          >
            <ArrowLeft />
          </Button>
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold tracking-tight">
              {isNew ? t("chat.automations.newTitle") : automation.name}
            </h2>
            {!isNew && (
              <p className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
                <span>{t("chat.automations.runCount", { count: automation.invocation_count })}</span>
                <span aria-hidden>·</span>
                <span>
                  {t("chat.automations.lastRun")}{" "}
                  <RelativeTime value={automation.last_invoked_at} fallback={t("common.never")} />
                </span>
              </p>
            )}
          </div>
        </div>
        {!isNew && (
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => void onDelete()}>
              <Trash2 />
              {t("chat.automations.delete")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setRunOpen(true)}
              disabled={form.formState.isDirty}
              title={form.formState.isDirty ? t("chat.automations.saveBeforeRun") : undefined}
              data-testid="automation-run"
            >
              <Play />
              {t("chat.automations.run")}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        <Panel>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="name"
              label={t("chat.automations.fields.name")}
              required
              autoFocus={isNew}
            />
            <SelectField
              control={form.control}
              name="agent_id"
              label={t("chat.automations.fields.agent")}
              required
              placeholder={t("chat.automations.fields.agentPlaceholder")}
              options={(agents.data ?? []).map((a) => ({
                value: a.id,
                label: a.name,
                description: a.model_name,
              }))}
            />
            <TextareaField
              control={form.control}
              name="description"
              label={t("chat.automations.fields.description")}
              rows={2}
              className="sm:col-span-2"
            />
          </div>
        </Panel>

        <ParamEditor />

        <section className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold">{t("chat.automations.templates.title")}</h3>
            <p className="text-muted-foreground text-sm">{t("chat.automations.templates.description")}</p>
          </div>
          {templates.map((tpl) => {
            const err = form.formState.errors[tpl.name]
            return (
              <FieldShell
                key={tpl.name}
                id={`tpl-${tpl.name}`}
                label={tpl.label}
                description={tpl.hint}
                error={err?.type === "server" ? err : undefined}
              >
                <TemplateEditor
                  id={`tpl-${tpl.name}`}
                  value={tpl.value}
                  onChange={(v) =>
                    form.setValue(tpl.name, v, {
                      shouldDirty: true,
                      shouldValidate: form.formState.isSubmitted,
                    })
                  }
                  schema={watchedSchema}
                  rows={tpl.rows}
                  invalid={!!err}
                  placeholder={tpl.placeholder}
                />
              </FieldShell>
            )
          })}
        </section>

        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">{t("chat.automations.apiAccess.title")}</h3>
            <p className="text-muted-foreground text-sm">{t("chat.automations.apiAccess.description")}</p>
          </div>
          <SwitchField
            control={form.control}
            name="api_invocation_enabled"
            variant="card"
            icon={KeyRound}
            label={t("chat.automations.apiAccess.enable")}
            description={t("chat.automations.apiAccess.enableHint")}
          />
          {!isNew && automation.api_invocation_enabled && apiEnabled && (
            <Panel>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="text-[0.8125rem] font-medium">{t("chat.automations.apiAccess.url")}</div>
                  <div className="bg-muted/50 flex items-center gap-1 rounded-lg border p-1 pl-3">
                    <code className="min-w-0 flex-1 truncate font-mono text-[0.8rem]" data-testid="hook-url">
                      {hookUrl(automation.invocation_public_id)}
                    </code>
                    <CopyButton value={hookUrl(automation.invocation_public_id)} />
                  </div>
                </div>
                <CodeBlock
                  title={t("chat.automations.apiAccess.example")}
                  language="bash"
                  wrap
                  className="[&_pre]:[overflow-wrap:anywhere]"
                  code={automationCurl(hookUrl(automation.invocation_public_id), watchedSchema)}
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-muted-foreground text-xs">
                    {automation.has_invocation_secret
                      ? t("chat.automations.apiAccess.secretSet")
                      : t("chat.automations.apiAccess.noSecret")}
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={() => void onRotate()}>
                    <RefreshCw />
                    {t("chat.automations.rotate")}
                  </Button>
                </div>
              </div>
            </Panel>
          )}
          {!isNew && automation.api_invocation_enabled && !apiEnabled && (
            <p className="text-warning text-xs">{t("chat.automations.apiAccess.disableWarning")}</p>
          )}
        </section>

        <FormRootError form={form} />
        {isNew && (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onBack}>
              {t("common.cancel")}
            </Button>
            <SubmitButton form={form}>{t("chat.automations.create")}</SubmitButton>
          </div>
        )}
      </div>
      {!isNew && <UnsavedChangesBar form={form} onDiscard={() => form.reset(toValues(automation))} />}

      {!isNew && <RunDialog automation={automation} open={runOpen} onOpenChange={setRunOpen} />}
    </FormRoot>
  )
}

// ---------------------------------------------------------------- run

function RunDialog({
  automation,
  open,
  onOpenChange,
}: {
  automation: Automation
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const invoke = useInvokeAutomation()
  const [values, setValues] = React.useState<Record<string, string>>({})
  const [files, setFiles] = React.useState<Record<string, File>>({})
  const [missing, setMissing] = React.useState<string[]>([])
  const [result, setResult] = React.useState<InvokeResult | null>(null)
  const resetInvoke = invoke.reset
  React.useEffect(() => {
    if (!open) return
    setValues({})
    setFiles({})
    setMissing([])
    setResult(null)
    resetInvoke()
  }, [open, resetInvoke])

  const error = invoke.error
  const fieldErrors = isApiError(error) ? (error.fields ?? {}) : {}
  const generalError = error && !(isApiError(error) && error.fields) ? error : null

  const run = async () => {
    const miss = automation.parameter_schema
      .filter(
        (p) =>
          p.required && (p.type === "file" ? !files[p.name] : !(values[p.name] ?? "").trim() && !p.default),
      )
      .map((p) => p.name)
    setMissing(miss)
    if (miss.length) return
    const clean = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ""))
    try {
      setResult(await invoke.mutateAsync({ id: automation.id, values: clean, files }))
    } catch {
      // shown inline
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !invoke.isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("chat.automations.runTitle", { name: automation.name })}</DialogTitle>
          <DialogDescription>
            {t("chat.automations.runDescription", { agent: automation.agent_name })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[60vh] gap-4 overflow-y-auto pr-1">
          {automation.parameter_schema.length === 0 && (
            <p className="text-muted-foreground text-sm">{t("chat.automations.noParams")}</p>
          )}
          {automation.parameter_schema.map((p) => {
            const id = `run-${p.name}`
            const err =
              fieldErrors[p.name] ?? (missing.includes(p.name) ? t("validation.required") : undefined)
            return (
              <FieldShell
                key={p.name}
                id={id}
                label={
                  <span className="flex items-center gap-1.5">
                    {p.label || p.name}
                    <code className="text-muted-foreground font-mono text-[0.6875rem] font-normal">
                      {p.name}
                    </code>
                  </span>
                }
                required={p.required}
                error={err ? { message: err } : undefined}
              >
                {p.type === "file" ? (
                  <Input
                    id={id}
                    type="file"
                    aria-invalid={!!err || undefined}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      setFiles((cur) => {
                        const next = { ...cur }
                        if (f) next[p.name] = f
                        else delete next[p.name]
                        return next
                      })
                    }}
                  />
                ) : p.type === "number" ? (
                  <Input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    placeholder={p.default}
                    value={values[p.name] ?? ""}
                    aria-invalid={!!err || undefined}
                    onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.value }))}
                  />
                ) : (
                  <Textarea
                    id={id}
                    rows={2}
                    placeholder={p.default}
                    value={values[p.name] ?? ""}
                    aria-invalid={!!err || undefined}
                    className="min-h-10"
                    onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.value }))}
                  />
                )}
              </FieldShell>
            )
          })}
          {generalError && (
            <p className="text-destructive text-sm" role="alert">
              {describeError(generalError, t)}
            </p>
          )}
          {invoke.isPending && (
            <div className="text-muted-foreground flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm">
              <Spinner />
              {t("chat.automations.running")}
            </div>
          )}
          {result && (
            <div className="space-y-2" data-testid="automation-output">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-sm font-medium">{t("chat.automations.output")}</h4>
                <StatusBadge tone={result.status === "completed" ? "success" : "danger"} dot>
                  {result.status}
                </StatusBadge>
                <code className="text-muted-foreground ml-auto font-mono text-[0.6875rem]">
                  {result.run_uid}
                </code>
                <CopyButton value={result.output} size="icon-xs" />
              </div>
              {result.error && <p className="text-destructive text-sm">{result.error}</p>}
              {result.output ? (
                <div className="bg-muted/30 max-h-80 overflow-y-auto rounded-lg border p-3">
                  <Markdown size="sm">{result.output}</Markdown>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">{t("chat.automations.noOutput")}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={invoke.isPending}>
            {t("chat.automations.close")}
          </Button>
          <Button onClick={() => void run()} disabled={invoke.isPending} data-testid="automation-run-submit">
            {invoke.isPending ? <Spinner /> : <Play />}
            {result ? t("chat.automations.runAgain") : t("chat.automations.run")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
